import { useCustom } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { Barcode, QrCode, Trash2, Truck, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";

import { CodeSheet, type CodeRequest } from "@/components/CodeSheet";
import { MoveFeed } from "@/components/MoveFeed";
import { SpisanieFeed } from "@/components/SpisanieFeed";
import { SpisanieSheet } from "@/components/SpisanieSheet";
import { query } from "@/lib/api";
import {
  errorMessage,
  moveWord,
  positionWord,
  reasonWord,
  stamp,
} from "@/lib/format";
import { pageOne } from "@/lib/loaded";
import { placeLabel } from "@/lib/places";
import { DATA_QUERIES } from "@/lib/providers";
import type {
  PlaceCard,
  PlaceHistory,
  PlaceLine,
  PlacePast,
  PlaceSpisanie,
} from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

/** Сколько строк ленты показывать сразу и сколько добавляет одно «ещё». */
const PAGE = 10;

/** Потолок ленты на стороне сервиса (`crm/move.py::HISTORY_MAX`). */
const HISTORY_MAX = 200;

const TH =
  "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated " +
  "px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg";

/** Позиция, которой сужена лента внизу. Общая для обеих половин таблицы. */
type Pick = { card_id: number; barcode: string; size: string };

/**
 * КАРТОЧКА МЕСТА — единственная карточка одной точки склада.
 *
 * Три ответа, и вместе они и есть место (решение владельца 11.09.26: «это
 * место, которое описывает, что сейчас здесь, что было и какие есть
 * перемещения — неважно, привязано ли к боксу либо к месту»):
 *
 *     что лежит     позиции места; у короба — с полкой ВБ
 *     что лежало    позиции, уже уехавшие отсюда, с датой изъятия
 *     что было      лента перемещений через это место
 *
 * Позиция стоит РОВНО В ОДНОМ из двух списков, и решает это остаток, а не
 * история, — то же правило, по которому раздел «Баркод» делит места на «где
 * лежит» и «где лежал».
 *
 * ЗДЕСЬ БЫЛО ДВЕ КАРТОЧКИ. До 11.09.26 короб открывался своей
 * (`/boxes/<место>`), а место — этой; у короба выходило два адреса на один
 * `placeID`, а у стола подбора и торгового зала состава не было вовсе. Теперь
 * адрес один, а `/boxes/<место>` уводит сюда.
 *
 * Короб отличается ровно одним — у него спрошена ПОЛКА ВБ, и колонка «На полке
 * ВБ» есть только у него: «вернёт на полку» — вопрос про то, что короб повезёт,
 * и у торгового зала он смысла не имеет.
 *
 * ВМЕСТО ИМЕНИ ТОВАРА — ФОТОГРАФИЯ. Имя место не различает: у пяти позиций
 * короба 38030 оно дословно одно и то же — «Платье вечернее праздничное сетка»
 * (замер 10.09.26), — а человек стоит над коробом с вещью в руках.
 */
export function PlacePage() {
  const { placeId = "" } = useParams();
  // Какой код показан во весь экран. `null` — ни одного.
  const [code, setCode] = useState<CodeRequest | null>(null);
  // Позиция, которую списывают. `null` — окно закрыто, и это ЕДИНСТВЕННОЕ
  // состояние, из которого списание не может случиться промахом пальца.
  const [writeOff, setWriteOff] = useState<PlaceLine | null>(null);
  // Позиция, которой сужена лента. Живёт ЗДЕСЬ, а не внутри таблицы:
  // выбирают её в одной части экрана, а отвечает на выбор другая.
  const [pick, setPick] = useState<Pick | null>(null);
  // Новое место — ни отбора ленты, ни открытого окна списания: позиция была
  // про ПРОШЛОЕ место, и списать её отсюда нельзя.
  useEffect(() => {
    setPick(null);
    setWriteOff(null);
  }, [placeId]);
  const historyRef = useRef<HTMLElement>(null);
  const queries = useQueryClient();

  const placeQuery = useCustom<PlaceCard>({
    url: `/api/places/${encodeURIComponent(placeId)}`,
    method: "get",
  });
  useSessionGuard(placeQuery.query.error);

  // Местом считается только payload с НОМЕРОМ места: `result.data` у Refine
  // существует и до загрузки — пустым объектом (см. `lib/loaded.ts`). Признак
  // здесь число, а не массив: место бывает пустым, и `lines: []` у него
  // законен.
  const place = pageOne(placeQuery.result.data, "place_id");
  const error = placeQuery.query.error;

  /**
   * Щелчок по строке: сузить ленту этой позицией — или снять сужение, если
   * щёлкнули по ней же. Одна и та же строка обязана и ставить, и снимать:
   * иначе снять можно только крестиком, которого в этот момент не видно, — он
   * уехал вниз вместе с лентой.
   */
  const choose = (row: PlaceLine | PlacePast) => {
    const next =
      pick && pick.card_id === row.card_id
        ? null
        : { card_id: row.card_id, barcode: row.barcode, size: row.size };
    setPick(next);
    // Лента стоит под составом, и на длинном месте ответ на щелчок оказывается
    // за краем экрана: без прокрутки клик неотличим от промаха. Но прокрутка
    // БЕЗУСЛОВНАЯ дёргала бы страницу и там, где лента и так на виду (разбор
    // 09.09.26 в разделе «Баркод»), поэтому едем, только если заголовок ленты
    // за краем окна.
    if (next) {
      requestAnimationFrame(() => {
        const seen = historyRef.current?.getBoundingClientRect();
        if (!seen) return;
        if (seen.top >= 0 && seen.top <= window.innerHeight - 120) return;
        historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  if (error && !place)
    return (
      <main className="p-4" data-component="PlacePage" data-annot="место">
        <p role="alert" className="text-[13px] text-err">
          {errorMessage(error)}
        </p>
      </main>
    );

  if (!place)
    return (
      <main className="p-4" data-component="PlacePage" data-annot="место">
        <p className="text-[13px] text-fg-muted">Открываю место…</p>
      </main>
    );

  const lines = place.lines ?? [];
  const past = place.past ?? [];
  const gone = place.spisanie ?? null;
  const wbOk = place.korob && (place.wb?.available ?? false);
  const missing = place.missing ?? 0;

  return (
    <main
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-5"
      data-component="PlacePage"
      data-annot={`место ${place.place_id}`}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[15px] font-semibold">
            {/* Зовётся место ОБЩИМ правилом (`lib/places.ts`): у короба это его
                номер, у прочих — имя как есть. Имя короба в CRM городу не равно
                и часто ему противоречит. */}
            {placeLabel(place)}
            <span className="ml-2 text-[13px] font-normal text-fg-muted">
              {place.city || "без города"}
            </span>
          </h1>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted">
            {/* Код места открывает QR: в CRM место находят СКАНОМ, и наряд
                «положить в короб 396» исполняется кодом 8809002396, а не
                числом 396. */}
            {place.place_barcode ? (
              <button
                type="button"
                onClick={() =>
                  setCode({
                    kind: "qr",
                    value: place.place_barcode ?? "",
                    title: `Код места · ${place.name || place.place_id}`,
                  })
                }
                title="Показать QR с кодом места"
                data-annot="код места"
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-ui)] px-1 py-0.5 -mx-1 tnum hover:bg-bg-hover"
              >
                {place.place_barcode}
                <QrCode size={13} className="flex-none" />
              </button>
            ) : (
              <span>без кода места</span>
            )}
            {place.group ? <span>· {place.group}</span> : null}
            {place.status ? <span>· {place.status}</span> : null}
            {/* Имя из CRM целиком — оно есть даже у короба, которого зовут
                номером, и на нём стоит вся переписка со складом. */}
            {place.korob && place.name ? <span>· {place.name}</span> : null}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <dl data-annot="сводка" className="flex flex-wrap gap-2">
            <Metric label="Позиций" value={place.barcodes} />
            <Metric
              label="Штук"
              value={place.units}
              // Вещи без карточки маркетплейса названы вслух: без этой подписи
              // место «Свободный» с 11 908 вещами и ни одной позицией
              // выглядело бы сломанным экраном (замер 11.09.26).
              note={
                place.units_unknown > 0
                  ? `${place.units_unknown} без карточки МП`
                  : ""
              }
            />
            {/* Ради этого числа короб и везут. Показывается только у короба:
                у зала «вернёт на полку» вопроса не составляет. Прочерк, а не
                ноль, когда зеркало кабинета ВБ недоступно. */}
            {place.korob ? (
              <Metric
                label="Вернёт на полку"
                value={wbOk ? missing : "—"}
                note={
                  wbOk
                    ? `${positionWord(missing)} из ${place.barcodes}`
                    : "остатки ВБ недоступны"
                }
                accent
              />
            ) : null}
            {/* Сколько разных позиций через место уже прошло и здесь не
                осталось. Число про ПРОШЛОЕ, поэтому стоит последним. */}
            <Metric
              label="Уехало"
              value={past.length}
              note={`${positionWord(past.length)} отсюда забрали`}
            />
            {/* Сколько вещей отсюда СПИСАЛИ. Число про место-ИСТОЧНИК: оно
                говорит, сколько с этой точки склада ушло насовсем и по скольким
                разным причинам. Ноль здесь — законный ответ, и он тоже стоит
                числом: «отсюда не списывали» — это хорошая новость про место. */}
            <Metric
              label="Списано отсюда"
              value={gone?.units ?? 0}
              note={
                gone && gone.categories.length > 0
                  ? `${gone.categories.length} ${reasonWord(gone.categories.length)}`
                  : "ни одного списания"
              }
            />
          </dl>

          {/* Короб отгрузки FBS — ссылкой: там своя половина ответа (поставка,
              акт, «ждут списания»), которой у места нет. Признак считает
              сервис по группам самих разделов. */}
          {place.fbs ? (
            <Link
              to={`/fbs/${place.place_id}`}
              data-annot="карточка fbs"
              className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
            >
              <Truck size={14} className="flex-none text-fg-muted" />
              Отгрузка FBS
            </Link>
          ) : null}
        </div>
      </div>

      <section data-annot="состав">
        <div className="overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
          <table
            data-annot="таблица"
            className="w-full border-collapse text-[13px]"
          >
            <caption className="sr-only">
              Позиции места: сначала те, что лежат в нём сейчас, затем те, что
              из него уже уехали
            </caption>
            <thead className="hidden md:table-header-group">
              <tr>
                <th scope="col" className={TH + " w-[52px] text-left"}>
                  Фото
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Баркод
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Размер
                </th>
                <th scope="col" className={TH + " text-right"}>
                  Штук
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Когда
                </th>
                {place.korob ? (
                  <th scope="col" className={TH + " text-right"}>
                    На полке ВБ
                  </th>
                ) : null}
                {/* Колонка ДЕЙСТВИЯ, и она последняя намеренно: сначала человек
                    убеждается, что перед ним та вещь, и только потом руки
                    доходят до необратимого. */}
                <th scope="col" className={TH + " w-[92px] text-right"}>
                  <span className="sr-only">Списание</span>
                </th>
              </tr>
            </thead>

            {/* ГРУППА ПЕРВАЯ — что лежит. У короба сверху позиции, которых на
                ВБ нет: его открывают, чтобы понять, ЧТО именно он вернёт на
                витрину. */}
            <Group
              title="Лежит здесь"
              note={
                `${place.barcodes} ${positionWord(place.barcodes)} · ${place.units} шт` +
                (place.lines_capped ? " · показаны первые" : "")
              }
              empty="Здесь не лежит ни одной вещи."
              count={lines.length}
              cols={place.korob ? 7 : 6}
            >
              {lines.map((line) => (
                <Row
                  key={`here-${line.card_id}`}
                  row={line}
                  picked={pick?.card_id === line.card_id}
                  onPick={() => choose(line)}
                  onCode={setCode}
                  units={line.units}
                  when={line.placed}
                  shelf={place.korob ? <Shelf line={line} known={wbOk} /> : null}
                  // Списывают только то, что ЛЕЖИТ: у уехавшей позиции ноль
                  // штук, и списывать там нечего. Кнопка поэтому есть ровно у
                  // первой группы.
                  onWriteOff={() => setWriteOff(line)}
                />
              ))}
            </Group>

            {/* ГРУППА ВТОРАЯ — что лежало. Ноль штук здесь не пробел, а ответ:
                вещи этого размера здесь были и уехали, и главное про них —
                КОГДА. Порядок задаёт сервис: по дате последнего изъятия. */}
            <Group
              title="Уехало отсюда"
              note={
                past.length > 0
                  ? `${past.length} ${positionWord(past.length)} · свежие изъятия сверху`
                  : ""
              }
              empty="Отсюда ещё ничего не забирали."
              count={past.length}
              cols={place.korob ? 7 : 6}
            >
              {past.map((one) => (
                <Row
                  key={`past-${one.card_id}`}
                  row={one}
                  picked={pick?.card_id === one.card_id}
                  onPick={() => choose(one)}
                  onCode={setCode}
                  units={0}
                  when={one.last_out}
                  moves={one}
                  shelf={
                    place.korob ? <span className="text-fg-muted">—</span> : null
                  }
                />
              ))}
            </Group>
          </table>
        </div>
      </section>

      {/* ЧТО ОТСЮДА СПИСАЛИ — между составом и лентой намеренно: это уже не
          «что здесь есть», но ещё и не лента, а свод причин, по которым вещи
          отсюда уходили насовсем. */}
      {gone ? <SpisanieOut out={gone} placeId={place.place_id} /> : null}

      <History
        placeId={place.place_id}
        pick={pick}
        onClear={() => setPick(null)}
        ref={historyRef}
      />

      {code ? <CodeSheet request={code} onClose={() => setCode(null)} /> : null}

      {/* ОКНО СПИСАНИЯ. Состав места после него ДРУГОЙ, и лента тоже: списание
          уводит вещь перемещением. Поэтому перечитываются оба запроса экрана —
          показывать старый состав рядом со словами «списано» значило бы спорить
          с самим собой. */}
      {writeOff ? (
        <SpisanieSheet
          aim={{ kind: "place", place, line: writeOff }}
          onClose={() => setWriteOff(null)}
          onDone={() => queries.invalidateQueries({ queryKey: DATA_QUERIES })}
        />
      ) : null}
    </main>
  );
}

/**
 * Группа строк таблицы с собственным заголовком.
 *
 * Одна таблица на обе половины, а не две рядом: колонки у них те же, и
 * позиция, известная только историей, обязана выглядеть так же, как позиция с
 * остатком, — иначе два списка читались бы как два разных места. Разделяет их
 * подпись, а не разметка.
 */
function Group({
  title,
  note,
  empty,
  count,
  cols,
  children,
}: {
  title: string;
  note: string;
  empty: string;
  count: number;
  cols: number;
  children: React.ReactNode;
}) {
  return (
    <tbody className="block md:table-row-group">
      <tr className="block md:table-row">
        <th
          scope="colgroup"
          colSpan={cols}
          data-annot={`группа ${title}`}
          className="block border-b border-border bg-bg-elevated/60 px-2.5 py-1.5 text-left text-[12px] font-medium md:table-cell"
        >
          {title}
          {note ? (
            <span className="ml-2 font-normal text-fg-muted">{note}</span>
          ) : null}
        </th>
      </tr>
      {count === 0 ? (
        <tr className="block md:table-row">
          <td
            colSpan={cols}
            className="block px-2.5 py-3 text-[12.5px] text-fg-muted md:table-cell"
          >
            {empty}
          </td>
        </tr>
      ) : (
        children
      )}
    </tbody>
  );
}

/**
 * Строка позиции — одна на обе половины таблицы.
 *
 * ЩЕЛЧОК ПО СТРОКЕ сужает ленту внизу этим баркодом: состав отвечает «что
 * здесь лежит и лежало», лента — «что с ним происходило», и это две половины
 * одного разбора. У ячеек с собственным действием (фото ведёт в товар, баркод
 * показывает Code 128) всплытие остановлено: иначе одно нажатие делало бы два
 * дела сразу.
 */
function Row({
  row,
  units,
  when,
  shelf,
  moves,
  picked,
  onPick,
  onCode,
  onWriteOff,
}: {
  row: PlaceLine | PlacePast;
  units: number;
  when: string | null;
  /** `null` — место не короб, и колонки полки ВБ у таблицы нет вовсе. */
  shelf: React.ReactNode;
  moves?: PlacePast;
  picked: boolean;
  onPick: () => void;
  onCode: (code: CodeRequest) => void;
  /** Не задан — позиция здесь уже не лежит, и списывать нечего. */
  onWriteOff?: () => void;
}) {
  return (
    <tr
      data-barcode={row.barcode}
      // Адрес замечания — сам баркод: он находится в CRM, а «пятая строка» не
      // находится нигде.
      data-annot={`строка ${row.barcode}`}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPick();
        }
      }}
      tabIndex={0}
      aria-pressed={picked}
      title="Показать перемещения этого баркода через место"
      className={
        "mb-2 block cursor-pointer rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 " +
        (picked
          ? "border-fg/40 bg-bg-elevated md:bg-bg-elevated"
          : "md:hover:bg-bg-hover") +
        // Уехавшая позиция бледнее: она про прошлое места, а не про то, что в
        // нём лежит.
        (units === 0 ? " opacity-80" : "")
      }
    >
      <Cell label="Фото">
        <Photo row={row} />
      </Cell>
      <Cell label="Баркод">
        <button
          type="button"
          onClick={(e) => {
            // Клик по баркоду — про код, а не про строку: без остановки он
            // заодно сузил бы ленту, и человек получил бы два действия за
            // одно нажатие.
            e.stopPropagation();
            onCode({
              kind: "barcode",
              value: row.barcode,
              title: `Баркод · размер ${row.size || "—"}`,
            });
          }}
          title="Показать штрихкод Code 128"
          data-annot="баркод"
          className="flex items-center gap-1.5 rounded-[var(--radius-ui)] px-1 py-0.5 -mx-1 hover:bg-bg-hover"
        >
          <span className="tnum font-medium">{row.barcode}</span>
          <Barcode size={14} className="flex-none text-fg-muted" />
        </button>
      </Cell>
      <Cell label="Размер">{row.size || "—"}</Cell>
      <Cell label="Штук" align="text-right tnum">
        {/* Ноль у уехавшей позиции — ОТВЕТ, а не пробел: этот баркод здесь был,
            и его больше нет. */}
        {units}
      </Cell>
      <Cell label="Когда" align="tnum whitespace-nowrap">
        <span className="flex flex-col">
          <span>{stamp(when) || "—"}</span>
          {/* Сколько раз сюда клали и сколько раз отсюда брали. Числа разной
              природы, и разность их осмысленна: положили пять, забрали три —
              две вещи ушли не перемещением. */}
          {moves ? (
            <span className="text-[11px] text-fg-muted">
              +{moves.moves_in} / −{moves.moves_out}
            </span>
          ) : null}
        </span>
      </Cell>
      {shelf !== null ? (
        <Cell label="На полке ВБ" align="text-right tnum">
          {shelf}
        </Cell>
      ) : null}
      <Cell label="Списание" align="text-right">
        {onWriteOff ? (
          <button
            type="button"
            onClick={(e) => {
              // Как и у баркода: щелчок по кнопке — про списание, а не про
              // строку. Без остановки он заодно сузил бы ленту внизу, и человек
              // получил бы два действия за одно нажатие.
              e.stopPropagation();
              onWriteOff();
            }}
            title="Списать вещи этого баркода из этого места"
            data-annot="списать"
            className="inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-2 text-[12.5px] hover:border-err/60 hover:bg-err-bg"
          >
            <Trash2 size={13} className="flex-none text-fg-muted" />
            Списать
          </button>
        ) : (
          // Пусто, но ЯЧЕЙКА ЕСТЬ: у обеих групп таблицы колонки обязаны
          // совпадать, иначе вторая половина поедет на колонку влево.
          <span className="text-fg-muted">—</span>
        )}
      </Cell>
    </tr>
  );
}

/**
 * ЧТО ОТСЮДА СПИСАЛИ И ПОЧЕМУ — задача владельца 11.09.26.
 *
 * Место здесь ИСТОЧНИК, и в этом весь раздел. Списание уносит вещь в «<ГОРОД>
 * Списано», поэтому причина, показанная на приёмнике, отвечает не на тот
 * вопрос: «из этого короба списали вещь по категории „Не нашли“» — это про
 * КОРОБ, там её не оказалось, а на складе списания та же строка не значит
 * ничего, кроме «сюда всё и сносят» (замер 11.09.26: из «РНД Списано» не
 * списали ни разу, при том что в него — тысячи).
 *
 * ДВЕ ПОЛОВИНЫ, и они отвечают на разное. Причины с числами — итог по всей
 * истории места: «что здесь пропадает и сколько». Ниже — сами ОПЕРАЦИИ, и
 * каждая называет СЕРИЮ (замечание владельца 11.09.26: «не хватает информации,
 * какой серии списали, чтобы можно было кликнуть и открыть саму серию»).
 * Разбор пропажи идёт от вещи, а вещь на складе зовут её номером.
 *
 * Строки рисует ОБЩАЯ таблица списаний (`components/SpisanieFeed.tsx`) — та
 * же, что в разделе «Списание» и на карточке баркода. Колонки «Откуда» у неё
 * здесь нет: все строки об одном месте, и столбец повторял бы заголовок
 * экрана.
 *
 * Итог считается по ПРИЧИНАМ, а не по числу строк: строк показана страница, а
 * причины знают всю историю места — включая вещи без карточки маркетплейса,
 * которых у места «Свободный» все девять из девяти.
 */
function SpisanieOut({ out, placeId }: { out: PlaceSpisanie; placeId: number }) {
  return (
    <section data-annot="списано отсюда">
      <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
        Списано отсюда
        {out.units > 0 ? (
          <span className="text-[13px] font-normal text-fg-muted">
            {out.units} шт · {out.categories.length}{" "}
            {reasonWord(out.categories.length)}
          </span>
        ) : null}
      </h2>

      {out.units === 0 ? (
        <p
          data-annot="пусто"
          className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2 text-[13px] text-fg-muted"
        >
          Отсюда ничего не списывали.
        </p>
      ) : (
        <>
          {/* ПРИЧИНЫ — над списком и с числами: ими и меряется место. */}
          <ul data-annot="причины" className="mb-2 flex flex-wrap gap-2">
            {out.categories.map((one) => (
              <li
                key={`cat-${one.category_id}`}
                data-annot={`причина ${one.category ?? one.category_id}`}
                title={one.last_at ? `Последний раз ${stamp(one.last_at)}` : ""}
                className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2.5 py-1 text-[13px]"
              >
                {one.category ?? `Причина ${one.category_id}`}
                <span className="ml-1.5 tabular-nums text-fg-muted">
                  {one.units}
                </span>
              </li>
            ))}
          </ul>

          <SpisanieFeed
            rows={out.rows}
            caption="Списания из этого места: какая серия, что за товар, почему и когда"
            withCard
            withCategory
          />

          {/* Строк больше страницы — дальше их показывает РАЗДЕЛ «Списание»,
              суженный этим местом. Своей кнопки «ещё» у карточки нет намеренно:
              карточка отвечает на «что здесь происходит», а «покажи все три
              тысячи» — вопрос к журналу, где есть и отбор, и своя страница. */}
          {out.has_more ? (
            <p className="mt-2 text-[12.5px] text-fg-muted">
              Показаны свежие {out.rows.length} из {out.units}.{" "}
              <Link
                to={`/spisanie?place=${placeId}&category=0`}
                data-annot="все списания места"
                className="underline decoration-border underline-offset-2 hover:decoration-fg"
              >
                Все списания этого места
              </Link>
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Фотография товара — ВМЕСТО его имени (задача владельца 10.09.26).
 *
 * Имя место не различает: у пяти позиций короба 38030 оно дословно одно и то
 * же. Картинка ведёт в карточку товара — увидев в коробе вещь, спрашивают «а
 * сколько её у нас всего».
 *
 * Нет фотографии — остаётся ссылка с номером модели: строка обязана вести в
 * товар и тогда, когда картинку на маркетплейс не завели.
 */
function Photo({ row }: { row: PlaceLine | PlacePast }) {
  return (
    <Link
      to={`/products/${row.model_id}`}
      onClick={(e) => e.stopPropagation()}
      title={row.name || `Товар ${row.model_id}`}
      data-annot="фото"
      className="flex h-[54px] w-[40px] flex-none items-center justify-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg-subtle text-[10px] text-fg-muted"
    >
      {row.image_url ? (
        <img
          src={row.image_url}
          alt={row.name}
          referrerPolicy="no-referrer"
          loading="lazy"
          className="h-full w-full object-cover"
          onError={(e) => {
            // Битая ссылка на чужой картинке — обычное дело, и рамка с
            // номером модели честнее сломанного значка браузера.
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span className="tnum">{row.model_id}</span>
      )}
    </Link>
  );
}

/**
 * Лента перемещений через это место: что сюда приходило и что уходило.
 *
 * «Ещё» поднимает ПОТОЛОК одного запроса, а не склеивает страницы — тот же
 * приём, что в лентах серии и баркода: между двумя запросами вещь могут
 * сдвинуть, и склейка показала бы перемещение дважды.
 *
 * `pick` сужает ленту одним баркодом — так на щелчок по строке состава
 * отвечает часть экрана, которая умеет ответить. Сужает ЗАПРОС, а не
 * показанные строки: страница ленты — десять строк из потолка в двести, и
 * позиция, вынутая месяц назад, в первую десятку не попадает вовсе.
 */
function History({
  placeId,
  pick,
  onClear,
  ref,
}: {
  placeId: number;
  pick: Pick | null;
  onClear: () => void;
  ref?: React.Ref<HTMLElement>;
}) {
  const [limit, setLimit] = useState(PAGE);
  // Новое место — новая лента; новый баркод — тоже: десять строк, набранные
  // по всему месту, к отбору одной позиции отношения не имеют.
  useEffect(() => setLimit(PAGE), [placeId, pick?.card_id]);

  const historyQuery = useCustom<PlaceHistory>({
    url: `/api/places/${placeId}/history${query({
      limit,
      card: pick?.card_id,
    })}`,
    method: "get",
    queryOptions: {
      // ПРЕЖНИЕ СТРОКИ ОСТАЮТСЯ, пока едут новые. Без этого лента на время
      // запроса пустела, страница схлопывалась на высоту десяти строк — и
      // экран под рукой уезжал (разбор 09.09.26 в разделе «Баркод»).
      placeholderData: (prev) => prev,
    },
  });
  const loaded = historyQuery.result.data;
  const moves = loaded && Array.isArray(loaded.moves) ? loaded.moves : [];
  const busy = historyQuery.query.isFetching;
  const error = historyQuery.query.error;

  return (
    <section data-annot="история" ref={ref}>
      <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
        Что здесь было{" "}
        <span className="font-normal text-fg-muted">
          {busy ? "обновляю…" : `${moves.length} ${moveWord(moves.length)}`}
          {!busy && loaded?.has_more ? " · есть ещё" : ""}
        </span>
        {/* Отбор назван и снимается ЗДЕСЬ же: лента, суженная баркодом, внешне
            неотличима от полной, и без этой строки десять перемещений одной
            позиции читались бы как вся история места. */}
        {pick ? (
          <button
            type="button"
            onClick={onClear}
            data-annot="снять баркод"
            title="Показать перемещения по всему месту"
            className="flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg-elevated px-2 py-0.5 text-[12px] font-normal hover:bg-bg-hover"
          >
            <span className="tnum">{pick.barcode}</span>
            {pick.size ? ` · ${pick.size}` : ""}
            <X size={12} className="flex-none text-fg-muted" />
          </button>
        ) : null}
      </h2>

      {error ? (
        <div
          role="alert"
          className="rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-4 text-center text-[13px]"
        >
          {errorMessage(error)}
        </div>
      ) : moves.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          {busy
            ? "Загружаю…"
            : pick
              ? "Вещи этого баркода через место не проезжали."
              : "Через это место ещё ничего не проходило."}
        </div>
      ) : (
        <MoveFeed
          moves={moves}
          tasksKnown={loaded?.tasks_known ?? false}
          withSerial
          withCard
          caption="Перемещения через место: когда, какая вещь, откуда, куда и чьей рукой"
        />
      )}

      {loaded?.has_more && limit < HISTORY_MAX ? (
        <button
          type="button"
          onClick={() => setLimit((now) => Math.min(now + PAGE, HISTORY_MAX))}
          disabled={busy}
          data-annot="ещё"
          className="mt-2 h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-55"
        >
          {busy ? "Загружаю…" : `Ещё ${PAGE}`}
        </button>
      ) : null}
    </section>
  );
}

/**
 * Остаток позиции на складе ВБ. Показывается только у КОРОБА.
 *
 * «Нет» — не то же самое, что «не заведён»: первое значит пустую полку,
 * которую эта вещь закроет, второе — что товара на маркетплейсе нет вовсе и
 * поставкой он там не появится.
 */
function Shelf({ line, known }: { line: PlaceLine; known: boolean }) {
  if (!known) return <span className="text-fg-muted">—</span>;
  if (line.wb === null)
    return (
      <span className="text-[11.5px] text-fg-muted" title="Товар не заведён на ВБ">
        не на ВБ
      </span>
    );
  return (
    <span className="flex flex-col items-end">
      {line.wb.amount === 0 ? (
        <span className="font-semibold text-accent-text">нет</span>
      ) : (
        <span>{line.wb.amount}</span>
      )}
      {/* «Едет обратно» — возврат от покупателя. Наличием он не считается
          (полка пуста уже сейчас), но видеть его нужно: эта дыра закроется и
          сама. */}
      {line.wb.from_client > 0 ? (
        <span
          className="text-[11px] text-fg-muted"
          title="Едет обратно возвратом от покупателя"
        >
          +{line.wb.from_client} обратно
        </span>
      ) : null}
    </span>
  );
}

function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      data-annot={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}

/** Плитка шапки: одно число и подпись под ним. */
function Metric({
  label,
  value,
  note,
  accent,
}: {
  label: string;
  value: string | number;
  note?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2">
      <dt className="text-[11px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "text-[19px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
      </dd>
      {note ? <p className="text-[11px] text-fg-muted">{note}</p> : null}
    </div>
  );
}
